import {
  AXIS_CHAR_WIDTH_RATIO,
  AXIS_LABEL_LENGTH_DEFAULT,
  AXIS_MAX_RELATIVE_VARIANCE,
  AXIS_MIN_LABEL_PADDING_PX,
  AXIS_MIN_SPACING_FOR_APPROXIMATE,
  AXIS_MIN_TICK_COUNT,
  AXIS_MIN_TICK_SPACING_PX,
  AXIS_Y_MIN_TICK_HEIGHT_PX,
} from '../../constants';

import { getChartFontSize } from './getChartFontSize';

/**
 * Utilities for calculating optimal tick counts and spacing for chart axes.
 * Ensures even spacing while respecting available width and avoiding label overlap.
 */

/**
 * Get estimated character width based on computed font size from CSS.
 * Called once per render to get the actual font size from the theme.
 *
 * @returns Estimated character width in pixels
 */
export function getEstimatedCharWidth(): number {
  const fontSize = getChartFontSize();
  return Math.round(fontSize * AXIS_CHAR_WIDTH_RATIO);
}

/**
 * Estimate the maximum number of tick labels that can fit without overlapping.
 * Uses actual formatted label length when available for accurate measurement.
 *
 * @param innerWidth - The inner width of the chart (excluding margins)
 * @param maxLabelLength - The length of the longest formatted label (in characters)
 * @returns Maximum number of ticks that can fit, or Infinity if width is unknown
 */
export function estimateMaxTicks(innerWidth: number | undefined, maxLabelLength: number): number {
  if (!innerWidth || innerWidth <= 0) {
    return Infinity; // no constraint if width unknown
  }

  const charWidth = getEstimatedCharWidth();
  const estimatedLabelWidth = charWidth * maxLabelLength;
  const minSpacing = Math.max(estimatedLabelWidth, AXIS_MIN_TICK_SPACING_PX);

  return Math.floor(innerWidth / minSpacing);
}

/**
 * Find the longest formatted label from a set of values.
 *
 * @param values - Array of x-axis values (numbers or strings)
 * @param formatter - Function to format values into label strings
 * @returns Length of the longest formatted label
 */
export function findMaxLabelLength(
  values: Array<number | string>,
  formatter: (value: number | string) => string,
): number {
  if (values.length === 0) {
    return AXIS_LABEL_LENGTH_DEFAULT;
  }

  let maxLength = 0;
  for (const value of values) {
    const label = formatter(value);
    if (label.length > maxLength) {
      maxLength = label.length;
    }
  }

  return maxLength || AXIS_LABEL_LENGTH_DEFAULT;
}

/**
 * Deduplicates adjacent tick values that format to the same label.
 * Only groups values that are next to each other in the sorted array and have the same
 * formatted string, returning one representative value per group (the middle value
 * for best visual centering).
 *
 * This prevents duplicate x-axis tick labels when data contains multiple timestamps
 * that format to the same date string (e.g., multiple times on the same day all
 * showing "Jan 1"), while preserving labels that happen to be the same but are
 * far apart (e.g., "Jan 1, 2024" and "Jan 1, 2025" both showing "Jan 1").
 *
 * @param values - Sorted array of unique x values
 * @param formatter - Function to format values into label strings
 * @returns Array of representative values, one per unique adjacent label group
 */
export function deduplicateAdjacentTickLabels(values: number[], formatter: (value: number) => string): number[] {
  if (values.length === 0) {
    return [];
  }

  const result: number[] = [];
  let currentGroup: number[] = [values[0]];
  let currentLabel = formatter(values[0]);

  for (let i = 1; i < values.length; i++) {
    const value = values[i];
    const label = formatter(value);

    if (label === currentLabel) {
      // Same label as previous - add to current group
      currentGroup.push(value);
    } else {
      // Different label - finalize current group and start new one
      const middleIndex = Math.floor(currentGroup.length / 2);
      result.push(currentGroup[middleIndex]);

      currentGroup = [value];
      currentLabel = label;
    }
  }

  // Don't forget to add the last group
  const middleIndex = Math.floor(currentGroup.length / 2);
  result.push(currentGroup[middleIndex]);

  return result;
}

/**
 * Minimum number of ticks to display (first and last).
 * Used as fallback when no other tick count works without overlap.
 */
/**
 * Selects evenly-spaced tick values in pixel space (not array index space).
 * This is critical for data with irregular intervals (e.g., dense clusters with gaps).
 *
 * Algorithm:
 * 1. Start with t = n (max number of ticks)
 * 2. Find t evenly spaced ticks in PIXEL SPACE (not array index) with variance/delta check
 * 3. Check if pixel spacing variance is acceptable (≤25% relative variance)
 * 4. Check for overlap: For each adjacent pair of ticks, check if labels overlap
 * 5. If variance is too high OR overlap exists: Set t = t - 1, go back to step 2 (recalculate evenly spaced ticks)
 * 6. If t reaches AXIS_MIN_TICK_COUNT and still fails: Return [first, last]
 * 7. Return the t ticks that have acceptable variance and don't overlap
 *
 * This ensures ticks are evenly spaced visually (in pixel space), regardless of
 * how data points are distributed in time/value space.
 *
 * @param values - Array of all candidate tick values (must be sorted)
 * @param maxTickCount - Maximum number of ticks to display
 * @param scale - Scale function that maps values to pixel positions
 * @param formatter - Function to format tick values into label strings
 * @returns Array of tick values that are evenly spaced in pixel space and don't overlap
 */
export function selectEvenlySpacedTicksInPixelSpace<T extends number | string>(
  values: T[],
  maxTickCount: number,
  scale: (value: T) => number | undefined,
  formatter: (value: T) => string,
): T[] {
  if (values.length === 0) {
    return [];
  }

  if (values.length === 1) {
    return values;
  }

  // Always include first and last values
  if (values.length === 2 || maxTickCount === AXIS_MIN_TICK_COUNT) {
    return [values[0], values[values.length - 1]];
  }

  // PRE-COMPUTE ONCE: Map all values to pixel positions
  const pixelPositions: Array<{ value: T; pixel: number; index: number }> = [];
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    const pixel = scale(value);
    if (pixel !== undefined) {
      pixelPositions.push({ value, pixel, index: i });
    }
  }

  if (pixelPositions.length < 2) {
    // Fallback if we can't get pixel positions
    return [values[0], values[values.length - 1]];
  }

  // PRE-COMPUTE ONCE: Range values
  const firstPixel = pixelPositions[0].pixel;
  const lastPixel = pixelPositions[pixelPositions.length - 1].pixel;
  const pixelRange = lastPixel - firstPixel;

  if (pixelRange <= 0) {
    // All values map to the same pixel (shouldn't happen, but handle gracefully)
    return [values[0], values[values.length - 1]];
  }

  // PRE-COMPUTE ONCE: Formatted labels and their half-widths for overlap detection
  const charWidth = getEstimatedCharWidth();
  const labelData = new Map<T, { label: string; halfWidth: number }>();
  for (const pos of pixelPositions) {
    const label = formatter(pos.value);
    labelData.set(pos.value, { label, halfWidth: (label.length * charWidth) / 2 });
  }

  // Try tick counts from maxTickCount down to AXIS_MIN_TICK_COUNT, finding the largest that doesn't overlap
  let currentTickCount = Math.min(maxTickCount, pixelPositions.length);

  while (currentTickCount >= AXIS_MIN_TICK_COUNT) {
    // Calculate ideal spacing for this tick count
    const idealSpacing = pixelRange / (currentTickCount - 1);

    // Find evenly spaced ticks in pixel space for current tick count
    const candidateTicks = selectTicksAtEvenPixelSpacing(pixelPositions, currentTickCount, firstPixel, idealSpacing);

    // Check variance/delta in pixel spacing
    const hasAcceptableVariance = checkPixelSpacingVariance(candidateTicks, pixelPositions, idealSpacing);

    // Check for overlap using pre-computed label data (no formatter calls!)
    const hasOverlap = checkTicksOverlapWithPrecomputed(candidateTicks, pixelPositions, labelData);

    // If both variance and overlap checks pass, return these ticks
    if (hasAcceptableVariance && !hasOverlap) {
      return candidateTicks;
    }

    // Either variance too high or overlap detected - try with fewer ticks (n-1)
    // This will recalculate evenly spaced ticks for n-1 in the next iteration
    currentTickCount -= 1;
  }

  // Fallback: return first and last (AXIS_MIN_TICK_COUNT ticks)
  return [pixelPositions[0].value, pixelPositions[pixelPositions.length - 1].value];
}

/**
 * Find the closest pixel position to a target pixel using binary search.
 * Since pixelPositions is sorted by pixel value (monotonic scale), we can use binary search.
 *
 * @param pixelPositions - Sorted array of pixel positions
 * @param targetPixel - Target pixel position to find closest match for
 * @param usedIndices - Set of indices already used (to avoid duplicates)
 * @returns Closest unused pixel position, or null if none found
 */
function findClosestPixelPosition<T>(
  pixelPositions: Array<{ value: T; pixel: number; index: number }>,
  targetPixel: number,
  usedIndices: Set<number>,
): { value: T; pixel: number; index: number } | null {
  // Binary search to find insertion point
  let left = 0;
  let right = pixelPositions.length - 1;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (pixelPositions[mid].pixel < targetPixel) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  // Check neighbors (left-1, left, left+1) for closest unused point
  let bestCandidate: { value: T; pixel: number; index: number } | null = null;
  let bestDistance = Infinity;

  for (let i = Math.max(0, left - 1); i <= Math.min(pixelPositions.length - 1, left + 1); i++) {
    const pos = pixelPositions[i];
    if (usedIndices.has(pos.index)) {
      continue;
    }

    const distance = Math.abs(pos.pixel - targetPixel);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestCandidate = pos;
    }
  }

  return bestCandidate;
}

/**
 * Build a pixel lookup map for selected tick values only (optimized).
 * Only includes pixels for the tick values we actually need to check.
 *
 * @param tickValues - Array of selected tick values
 * @param pixelPositions - Array of all pixel positions (for lookup)
 * @returns Map of value -> pixel position
 */
function buildTickPixelsMap<T>(
  tickValues: T[],
  pixelPositions: Array<{ value: T; pixel: number; index: number }>,
): Map<T, number> {
  // Build lookup map only for selected tick values (smaller than iterating all positions)
  const tickPixels = new Map<T, number>();
  const tickValueSet = new Set(tickValues);

  for (const pos of pixelPositions) {
    if (tickValueSet.has(pos.value)) {
      tickPixels.set(pos.value, pos.pixel);
    }
  }

  return tickPixels;
}

/**
 * Check if the pixel spacing of selected ticks has acceptable variance.
 *
 * Prioritizes exact spacing (variance = 0%), but accepts approximate spacing
 * when the ideal spacing is large enough (>= MIN_SPACING_FOR_APPROXIMATE).
 * This ensures small datasets require exact spacing to avoid visible unevenness,
 * while large datasets can use approximate spacing since variance is negligible.
 *
 * @param tickValues - Array of selected tick values
 * @param pixelPositions - Array of all pixel positions (for lookup)
 * @param idealSpacing - Ideal spacing between ticks in pixels
 * @returns true if variance is acceptable (≤25%), false otherwise
 */
function checkPixelSpacingVariance<T>(
  tickValues: T[],
  pixelPositions: Array<{ value: T; pixel: number; index: number }>,
  idealSpacing: number,
): boolean {
  if (tickValues.length < 2) {
    return true; // No variance to check
  }

  // Build pixel lookup only for selected tick values (optimized)
  const tickPixels = buildTickPixelsMap(tickValues, pixelPositions);

  // Calculate actual spacings between adjacent ticks
  const spacings: number[] = [];
  for (let i = 0; i < tickValues.length - 1; i++) {
    const currentPos = tickPixels.get(tickValues[i]);
    const nextPos = tickPixels.get(tickValues[i + 1]);
    if (currentPos !== undefined && nextPos !== undefined) {
      spacings.push(Math.abs(nextPos - currentPos));
    }
  }

  if (spacings.length === 0) {
    return true; // Can't calculate variance
  }

  // Early return for 2 ticks: only one spacing to check
  if (tickValues.length === 2) {
    // For 2 ticks, spacing should be close to ideal (allow small floating point differences)
    const spacing = spacings[0];
    return Math.abs(spacing - idealSpacing) < 1;
  }

  // For exact spacing (variance = 0%), all spacings should equal idealSpacing
  // For approximate spacing, check relative variance
  const minSpacing = Math.min(...spacings);
  const maxSpacing = Math.max(...spacings);

  // If ideal spacing is large enough, approximate spacing is acceptable
  if (idealSpacing >= AXIS_MIN_SPACING_FOR_APPROXIMATE) {
    // For large ideal spacing, check relative variance
    // Variance = (max - min) / idealSpacing
    const relativeVariance = (maxSpacing - minSpacing) / idealSpacing;
    return relativeVariance <= AXIS_MAX_RELATIVE_VARIANCE;
  }

  // For small ideal spacing, require exact spacing (all spacings equal)
  // Allow small floating point differences
  return maxSpacing - minSpacing < 1;
}

/**
 * Check if tick labels overlap using pre-computed label data.
 * This avoids repeated formatter() calls.
 *
 * @param tickValues - Array of tick values to check
 * @param pixelPositions - Array of all pixel positions (for lookup)
 * @param labelData - Pre-computed map of value -> { label, halfWidth }
 * @returns true if any adjacent labels would overlap, false otherwise
 */
function checkTicksOverlapWithPrecomputed<T>(
  tickValues: T[],
  pixelPositions: Array<{ value: T; pixel: number; index: number }>,
  labelData: Map<T, { label: string; halfWidth: number }>,
): boolean {
  if (tickValues.length < 2) {
    return false;
  }

  // Build pixel lookup only for selected tick values (optimized)
  const tickPixels = buildTickPixelsMap(tickValues, pixelPositions);

  for (let i = 0; i < tickValues.length - 1; i++) {
    const currentPos = tickPixels.get(tickValues[i]);
    const nextPos = tickPixels.get(tickValues[i + 1]);
    if (currentPos === undefined || nextPos === undefined) {
      continue;
    }

    // Look up pre-computed half-widths (no formatter calls!)
    const currentData = labelData.get(tickValues[i]);
    const nextData = labelData.get(tickValues[i + 1]);

    const minDistance = (currentData?.halfWidth ?? 0) + (nextData?.halfWidth ?? 0) + AXIS_MIN_LABEL_PADDING_PX;
    const actualDistance = Math.abs(nextPos - currentPos);

    if (actualDistance < minDistance) {
      return true;
    }
  }

  return false;
}

/**
 * Select n ticks that are evenly spaced in pixel space.
 * For each ideal pixel position, find the closest data point using binary search.
 * Always includes first and last data points.
 *
 * @param pixelPositions - Sorted array of pixel positions
 * @param tickCount - Number of ticks to select
 * @param firstPixel - Pixel position of first value
 * @param idealSpacing - Ideal spacing between ticks in pixels
 * @returns Array of selected tick values
 */
function selectTicksAtEvenPixelSpacing<T>(
  pixelPositions: Array<{ value: T; pixel: number; index: number }>,
  tickCount: number,
  firstPixel: number,
  idealSpacing: number,
): T[] {
  if (tickCount <= 0) {
    return [];
  }

  if (tickCount === 1) {
    return [pixelPositions[0].value];
  }

  if (tickCount === 2) {
    return [pixelPositions[0].value, pixelPositions[pixelPositions.length - 1].value];
  }

  const selectedTicks: T[] = [];
  const usedIndices = new Set<number>();

  // Always include first
  selectedTicks.push(pixelPositions[0].value);
  usedIndices.add(pixelPositions[0].index);

  // Select intermediate ticks at evenly-spaced pixel positions
  for (let i = 1; i < tickCount - 1; i++) {
    const targetPixel = firstPixel + i * idealSpacing;

    // Find the closest data point to this ideal pixel position using binary search
    const bestCandidate = findClosestPixelPosition(pixelPositions, targetPixel, usedIndices);

    if (bestCandidate) {
      selectedTicks.push(bestCandidate.value);
      usedIndices.add(bestCandidate.index);
    }
  }

  // Always include last (if not already included)
  const lastPos = pixelPositions[pixelPositions.length - 1];
  if (!usedIndices.has(lastPos.index)) {
    selectedTicks.push(lastPos.value);
  }

  return selectedTicks;
}

/**
 * Selects evenly-spaced tick values that don't overlap in pixel space.
 *
 * Always uses pixel-space-based selection to handle irregular intervals correctly.
 * This ensures ticks are evenly spaced visually (in pixel space), regardless of
 * how data points are distributed in time/value space.
 *
 * @param values - Array of all candidate tick values
 * @param maxTickCount - Maximum number of ticks to display
 * @param scale - Scale function that maps values to pixel positions
 * @param formatter - Function to format tick values into label strings
 * @returns Array of tick values that don't overlap
 */
export function selectNonOverlappingTicks<T extends number | string>(
  values: T[],
  maxTickCount: number,
  scale: (value: T) => number | undefined,
  formatter: (value: T) => string,
): T[] {
  if (values.length === 0) {
    return [];
  }

  if (values.length === 1) {
    return values;
  }

  return selectEvenlySpacedTicksInPixelSpace(values, maxTickCount, scale, formatter);
}

/**
 * Estimates an appropriate number of Y-axis ticks for a given container height.
 *
 * Uses AXIS_Y_MIN_TICK_HEIGHT_PX as the minimum vertical space per tick. The result is
 * a hint passed to calculateNiceDomainWithTicks, which may produce a slightly different
 * count to land on nice round values.
 *
 * @param containerHeight - Total height of the chart container in pixels (including margins)
 * @returns Estimated number of ticks (always at least AXIS_MIN_TICK_COUNT)
 */
export function estimateYAxisNumTicks(containerHeight: number): number {
  if (containerHeight <= 0) {
    return AXIS_MIN_TICK_COUNT;
  }
  return Math.max(AXIS_MIN_TICK_COUNT, Math.round(containerHeight / AXIS_Y_MIN_TICK_HEIGHT_PX));
}
